import React, { useState, useCallback } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, getSortedRowModel, PaginationState, getPaginationRowModel } from "@tanstack/react-table";
import { Table, Space, Pagination } from '@mantine/core';

/** 
* Definition of columns for the table 
* Property names should match the "accessor" in the table columns
*/
export interface TrademarksTableColumns { 
    edit: JSX.Element;
    delete: JSX.Element;
    title: string;
    registrationNumber: string;
    applicationNumber: string;
    owner: string;
    class: string;
    client: string;
    referenceNumber: string;
};

/** Props for the table component */
export interface ITableProps {
    data: TrademarksTableColumns[];
    pageSize?: number;
}

export default function TrademarksTable(props: ITableProps) {
    const [pageIndexOneIndexed, setPageIndexOneIndexed] = useState<number>(1);

    const { data } = props;
    const pageSize = props.pageSize ?? 20;
    const columnHelper = createColumnHelper<TrademarksTableColumns>();
    const columns = [
        columnHelper.accessor("edit", { header: "" }),
        columnHelper.accessor("delete", { header: "" }),
        columnHelper.accessor("title", { header: "Title" }),
        columnHelper.accessor("registrationNumber", { header: "Registration Number" }),
        columnHelper.accessor("applicationNumber", { header: "Application Number" }),
        columnHelper.accessor("owner", { header: "Owner" }),
        columnHelper.accessor("class", { header: "Class" }),
        columnHelper.accessor("client", { header: "Client" }),
        columnHelper.accessor("referenceNumber", { header: "Reference Number" }),
    ];

    const table = useReactTable({ 
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageIndex: 0, pageSize: pageSize } }
    });

    const onPageChange = useCallback((idxOneIndexed: number) => {
        table.setPageIndex(idxOneIndexed - 1);
        setPageIndexOneIndexed(idxOneIndexed);
    }, [table, setPageIndexOneIndexed]);

    return (
        <>
        <Table className="TrademarksTable">
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                    <th key={header.id}>
                        {header.isPlaceholder
                            ? null
                            : <div style={{ cursor: "pointer" }} onClick={header.column.getToggleSortingHandler()}>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                        }
                    </th>
                    ))}
                </tr>
            ))}
            </thead>

            <tbody>
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map((cell, index) => {
                            const style: any = {};
                            if (index === 0 || index === 1) { style["width"] = "1%"; }
                            return (
                                <td key={cell.id} style={style}>
                                    {cell.renderValue() as JSX.Element}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </Table>
        <Space h="md" />
        <Pagination page={pageIndexOneIndexed} onChange={onPageChange} total={Math.ceil(data.length / pageSize)} />
        </>
    );
}